import { useState } from 'react'
import { mfaAPI } from '../../services/mfaAPI'
import Button from '../UI/Button'
import Input from '../UI/Input'
import Card, { CardHeader, CardTitle, CardDescription, CardContent } from '../UI/Card'
import toast from 'react-hot-toast'
import { Shield, Key } from 'lucide-react'

export default function MFAVerification({ sessionToken, onSuccess, onCancel, embedded = false }) {
  const [token, setToken] = useState('')
  const [backupCode, setBackupCode] = useState('')
  const [useBackupCode, setUseBackupCode] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleVerify = async () => {
    if (useBackupCode) {
      if (!backupCode || backupCode.length !== 8) {
        toast.error('Please enter a valid 8-digit backup code')
        return
      }
    } else {
      if (!token || token.length !== 6) {
        toast.error('Please enter a valid 6-digit code')
        return
      }
    }

    setLoading(true)
    try {
      // Verify MFA
      const verifyResponse = await mfaAPI.verifyLogin(
        sessionToken,
        useBackupCode ? null : token,
        useBackupCode ? backupCode : null
      )

      if (verifyResponse.data.success) {
        // Complete login
        const completeResponse = await mfaAPI.completeLogin(sessionToken)
        
        if (completeResponse.data.success) {
          toast.success('Login successful!')
          if (onSuccess) {
            onSuccess(completeResponse.data.data)
          }
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Invalid code. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const fields = (
    <div className="space-y-5">
      {!useBackupCode ? (
        <div className="space-y-2">
          <label className="auth-field">6-digit authenticator code</label>
          <Input
            type="text"
            inputMode="numeric"
            value={token}
            onChange={(e) => setToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            maxLength={6}
            className="auth-input text-center text-2xl font-mono tracking-[0.4em]"
            autoFocus
          />
          <button type="button" onClick={() => setUseBackupCode(true)} className="auth-link">
            Use a backup code instead
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <label className="auth-field flex items-center gap-2">
            <Key className="h-4 w-4" />
            8-digit backup code
          </label>
          <Input
            type="text"
            inputMode="numeric"
            value={backupCode}
            onChange={(e) => setBackupCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
            placeholder="00000000"
            maxLength={8}
            className="auth-input text-center text-2xl font-mono tracking-[0.35em]"
            autoFocus
          />
          <button type="button" onClick={() => setUseBackupCode(false)} className="auth-link">
            Use authenticator app instead
          </button>
        </div>
      )}
      <div className="flex gap-2">
        {onCancel && (
          <Button variant="outline" onClick={onCancel} disabled={loading} className="h-12 rounded-xl">
            Cancel
          </Button>
        )}
        <Button
          onClick={handleVerify}
          loading={loading}
          disabled={useBackupCode ? backupCode.length !== 8 : token.length !== 6}
          className="h-12 flex-1 rounded-xl text-[15px] font-semibold"
        >
          Verify
        </Button>
      </div>
    </div>
  )

  if (embedded) return fields

  return (
    <Card className="max-w-md mx-auto">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Shield className="h-6 w-6 text-primary-600" />
          <CardTitle>Multi-Factor Authentication</CardTitle>
        </div>
        <CardDescription>Enter the code from your authenticator app</CardDescription>
      </CardHeader>
      <CardContent>{fields}</CardContent>
    </Card>
  )
}

